import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Conversation, UpdateConversation } from "../../interfaces/conversations.type";
import { BaseMessage } from "../../interfaces/messages.type";
import { ConversationQuery } from "../../hooks/apis/conversations.api";

export interface SelectedConversaton {
    conversation_id: string;
    did: string;
    participants: string[];
    type: "direct" | "group";
    position?: number;
    search?: string;
    searched_message_id?: string;
    total_unread?: number;
    unread_messages_count?: number;
    unread?: boolean;
}

export interface ConversationState {
    data: Conversation[];
    selectedConversation: SelectedConversaton | null;
    draftParticipants: string[];
}

const initialState: ConversationState = {
    data: [],
    selectedConversation: null,
    draftParticipants: []
};

export const conversationSlice = createSlice({
    name: "conversation",
    initialState,
    reducers: {
        setConversations: (state, action: PayloadAction<{ data: Conversation[]; query: ConversationQuery; offset: number }>) => {
            if (action.payload.query.searchQuery && action.payload.query.searchQuery !== "" && action.payload.offset === 0) {
                if (action.payload.data.length > 0) {
                    const conversations = state.data.filter(item => item.did !== action.payload.data[0].did);
                    state.data = [...conversations, ...action.payload.data];
                } else {
                    state.data = [];
                }
                return;
            }
            action.payload.data.forEach(conversation => {
                const index = state.data.findIndex(item => item.id === conversation.id);
                // conversation.total_unread = Number(conversation.total_unread);
                if (index !== -1) {
                    state.data[index] = conversation;
                } else {
                    state.data.push(conversation);
                }
            });
        },
        setSelectedConversation: (state, action: PayloadAction<SelectedConversaton | null>) => {
            state.selectedConversation = action.payload;
        },
        setDraftParticipants: (state, action: PayloadAction<string[]>) => {
            state.draftParticipants = action.payload;
        },

        updateConversationsByIncomingMessage: (state, action: PayloadAction<BaseMessage>) => {
            if (!action.payload.conversation_id) return;
            const index = state.data.findIndex(item => item.id === action.payload.conversation_id);

            if (index !== -1) {
                const lastMessageTime = new Date(state.data[index].last_message_time);
                const messageTime = new Date(action.payload.timestamp);
                state.data[index].last_message = lastMessageTime > messageTime ? state.data[index].last_message : action.payload.body;
                state.data[index].last_message_time =
                    lastMessageTime > messageTime ? state.data[index].last_message_time : action.payload.timestamp;
                state.data[index].resolved_at = null;
                state.data[index].resolved = false;
                state.data[index].unread_messages_count =
                    action.payload.direction == "inbound" ? Number(state.data[index].unread_messages_count) + 1 : Number(state.data[index].unread_messages_count);
            } else {
                const participants = action.payload.conversation_participants?.length
                    ? action.payload.conversation_participants
                    : [action.payload.participant];
                state.data.push({
                    id: action.payload.conversation_id,
                    did: action.payload.did,
                    participants,
                    type: action.payload.conversation_type || (participants.length > 1 ? "group" : "direct"),
                    last_message: action.payload.body,
                    last_message_time: action.payload.timestamp,
                    unread_messages_count: action.payload.direction == "inbound" ? 1 : 0,
                    unread: false,
                    assigned_user: null,
                    resolved_at: null,
                    last_read_timestamp: new Date().toISOString(),
                    resolved: false
                });
            }
        },

        updateConversationByConversationEvent: (state, action: PayloadAction<UpdateConversation>) => {
            const index = state.data.findIndex(item => item.id === action.payload.conversation_id);
            if (index !== -1) {
                state.data[index] = {
                    ...state.data[index],
                    ...action.payload,
                    unread_messages_count: Object.hasOwnProperty.call(action.payload, "last_read_timestamp") ? 0 : state.data[index].unread_messages_count,
                    unread: Object.hasOwnProperty.call(action.payload, "unread") ? action.payload.unread || false : state.data[index].unread,
                    resolved: Object.hasOwnProperty.call(action.payload, "resolved_at")
                        ? action.payload.resolved_at !== null
                            ? true
                            : false
                        : state.data[index].resolved
                };
            }
        },

        addConversationSlice: (state, action: PayloadAction<Conversation>) => {
            const index = state.data.findIndex(item => item.id === action.payload.id);

            if (index === -1) {
                state.data.push(action.payload);
            }
        }
    }
});

export const {
    setConversations,
    setSelectedConversation,
    setDraftParticipants,
    updateConversationByConversationEvent,
    updateConversationsByIncomingMessage,
    addConversationSlice
} = conversationSlice.actions;

export default conversationSlice.reducer;
